import { api } from "@/lib/api-client";
import type {
  ApiCart,
  CartFees,
  PromoValidationResult,
  PromoCodeListItem,
  CartDraft,
  AddToCartPayload,
  UpdateCartItemPayload,
  CalculateCartPayload,
  ListPromoCodesParams,
  ValidatePromoPayload,
  ValidatePromoBeforeOrderPayload,
  CreateDraftPayload,
  ConfirmOrderPayload,
  ConfirmOrderResponse,
} from "@/types";

function toQuery(params: ListPromoCodesParams): string {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.search != null) qs.set("search", params.search);
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortOrder) qs.set("sortOrder", params.sortOrder);

  if (params.filter) {
    Object.entries(params.filter).forEach(([key, value]) => {
      qs.set(`filter[${key}]`, String(value));
    });
  }

  const query = qs.toString();
  return query ? `?${query}` : "";
}

function normalizePromoCodesResponse(input: unknown): PromoCodeListItem[] {
  if (Array.isArray(input)) return input as PromoCodeListItem[];
  if (!input || typeof input !== "object") return [];

  const candidate = input as {
    items?: unknown;
    data?: unknown;
    results?: unknown;
    promo_codes?: unknown;
  };

  if (Array.isArray(candidate.items)) return candidate.items as PromoCodeListItem[];
  if (Array.isArray(candidate.data)) return candidate.data as PromoCodeListItem[];
  if (Array.isArray(candidate.results)) return candidate.results as PromoCodeListItem[];
  if (Array.isArray(candidate.promo_codes)) return candidate.promo_codes as PromoCodeListItem[];

  return [];
}

export const cartService = {
  /** Normalize various possible backend shapes into ApiCart[] */
  // The backend may return: [] | { data: [] } | { items: [] } | { results: [] } | { carts: [] } | { <storeUlid>: cart }
  // This helper returns a safe array of ApiCart.
  normalizeCartsResponse: (input: unknown): ApiCart[] => {
    if (Array.isArray(input)) return input as ApiCart[];
    if (!input || typeof input !== "object") return [];

    const candidate = input as Record<string, unknown>;
    if (Array.isArray(candidate.items)) return candidate.items as ApiCart[];
    if (Array.isArray(candidate.data)) return candidate.data as ApiCart[];
    if (Array.isArray(candidate.results)) return candidate.results as ApiCart[];
    if (Array.isArray(candidate.carts)) return candidate.carts as ApiCart[];

    // If the response is an object whose values look like carts (have items array), collect them
    try {
      const values = Object.values(candidate);
      const carts = values.filter((v) => v && typeof v === "object" && Array.isArray((v as { items?: unknown }).items));
      if (carts.length > 0) return carts as ApiCart[];
    } catch (e) {
      // ignore
    }

    return [];
  },

  /** GET /cart/carts — all carts for the current customer */
  getAllCarts: async (token?: string) => {
    const response = await api.get<unknown>("/cart/carts", token ? { token } : undefined);
    return cartService.normalizeCartsResponse(response);
  },

  /** DELETE /cart/carts — clear every cart */
  clearAllCarts: (token?: string) =>
    api.delete<void>("/cart/carts", { token }),

  /** GET /cart/cart/{storeUlid} — cart for a specific store */
  getStoreCart: (storeUlid: string, token?: string) =>
    api.get<ApiCart>(`/cart/cart/${storeUlid}`, { token }),

  /** DELETE /cart/cart/{storeUlid} — clear a store's cart */
  clearStoreCart: (storeUlid: string, token?: string) =>
    api.delete<void>(`/cart/cart/${storeUlid}`, { token }),

  /** POST /cart/items — add a product to cart */
  addToCart: (payload: AddToCartPayload, token?: string) =>
    api.post<void>("/cart/items", payload, { token }),

  /** PUT /cart/{storeUlid}/items/{cartItemId} — update item quantity (0 = delete) */
  updateCartItem: (
    storeUlid: string,
    cartItemId: string,
    payload: UpdateCartItemPayload,
    token?: string
  ) => api.put<void>(`/cart/${storeUlid}/items/${cartItemId}`, payload, { token }),

  /** DELETE /cart/{storeUlid}/items/{cartItemId} — remove a single item */
  removeCartItem: (storeUlid: string, cartItemId: string, token?: string) =>
    api.delete<void>(`/cart/${storeUlid}/items/${cartItemId}`, { token }),

  /** POST /cart/{storeUlid}/calculate — calculate delivery, taxes, promo */
  calculateCart: (storeUlid: string, payload: CalculateCartPayload, token?: string) =>
    api.post<CartFees>(`/cart/${storeUlid}/calculate`, payload, { token }),

  /** POST /cart/promo/validate — validate a promo code */
  validatePromo: (payload: ValidatePromoPayload, token?: string) =>
    api.post<PromoValidationResult>("/cart/promo/validate", payload, { token }),

  /** POST /promo-codes/validate — validate promo before placing order */
  validatePromoBeforeOrder: (payload: ValidatePromoBeforeOrderPayload, token?: string) =>
    api.post<PromoValidationResult>("/promo-codes/validate", payload, token ? { token } : undefined),

  /** GET /promo-codes — list active public promo codes */
  listPublicPromoCodes: async (params: ListPromoCodesParams = {}, token?: string) => {
    const query = toQuery(params);
    const response = await api.get<unknown>(`/promo-codes${query}`, token ? { token } : undefined);
    return normalizePromoCodesResponse(response);
  },

  /** POST /cart/draft — create or update a draft */
  createDraft: (payload: CreateDraftPayload, token?: string) =>
    api.post<CartDraft>("/cart/draft", payload, { token }),

  /** GET /cart/draft/{storeUlid} — retrieve an existing draft */
  getDraft: (storeUlid: string, token?: string) =>
    api.get<CartDraft>(`/cart/draft/${storeUlid}`, { token }),

  /** DELETE /cart/draft/{storeUlid} — cancel a draft */
  cancelDraft: (storeUlid: string, token?: string) =>
    api.delete<void>(`/cart/draft/${storeUlid}`, { token }),

  /** POST /cart/confirm/{storeUlid} — confirm / place an order */
  confirmOrder: (storeUlid: string, payload: ConfirmOrderPayload, token?: string) =>
    api.post<ConfirmOrderResponse>(`/cart/confirm/${storeUlid}`, payload, { token }),
};
